import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "semantic-ui-react";

import { Inventory } from "../../../../../api";

import styles from "./PeriodSummary.module.scss";
import {
  buildExportFilename,
  buildMonthlySalesCsv,
  downloadCsv,
  formatCurrency,
  formatMonthTitle,
  getMonthOptions,
  getMonthlySalesDetailResponse,
  getProductBreakdownRows,
} from "./PeriodSummary.form";

const MONTH_OPTIONS = getMonthOptions();
const BREAKDOWN_VISIBLE_LIMIT = 8;

/*
 * ← Resumen por período: a diferencia de las tarjetas de arriba (que
 * muestran el mes actual) y de la gráfica de ganancias (últimos 6
 * meses), acá el admin elige un mes específico dentro del año -- el
 * año NO se elige aquí, es el filtro general de toda la página
 * (Home.js lo pasa como prop), para no tener dos selectores de año
 * independientes mostrando cosas distintas. Ve cuánto se invirtió
 * (costo de lo vendido) y cuánto se ganó ese mes, con desglose por
 * producto y opción de exportar a CSV. Usa el mismo endpoint que el
 * detalle de "Artículos vendidos" (Inventory.getMonthlySalesDetail),
 * que ahora también trae costo/ganancia por producto y los totales del
 * mes.
 */
export function PeriodSummary({ year }) {
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({
    unitsSold: 0,
    revenue: 0,
    cost: 0,
    profit: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAllItems, setShowAllItems] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    async function loadPeriod() {
      try {
        setIsLoading(true);
        setError("");
        setShowAllItems(false);

        const inventoryController = new Inventory();
        const result = await inventoryController.getMonthlySalesDetail(
          year,
          month
        );

        if (isCancelled) {
          return;
        }

        const response = getMonthlySalesDetailResponse(result);

        setItems(response.items);
        setTotals(response.totals);
      } catch (errorResponse) {
        if (isCancelled) {
          return;
        }

        console.error("getMonthlySalesDetail error:", errorResponse);

        setItems([]);
        setTotals({ unitsSold: 0, revenue: 0, cost: 0, profit: 0 });
        setError(
          errorResponse?.msg ||
            "No fue posible cargar el resumen de este período."
        );
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadPeriod();

    return () => {
      isCancelled = true;
    };
  }, [year, month]);

  const handleMonthChange = useCallback((event) => {
    setMonth(Number(event.target.value));
    setShowAllItems(false);
  }, []);

  const handleExport = useCallback(() => {
    const csvContent = buildMonthlySalesCsv(items, totals, year, month);

    downloadCsv(buildExportFilename(year, month), csvContent);
  }, [items, totals, year, month]);

  const hasData = totals.unitsSold > 0;

  const breakdownRows = useMemo(
    () => getProductBreakdownRows(items, totals),
    [items, totals]
  );

  const visibleBreakdownRows = showAllItems
    ? breakdownRows
    : breakdownRows.slice(0, BREAKDOWN_VISIBLE_LIMIT);

  return (
    <section className={styles.PeriodSummary}>
      <header className={styles.Header}>
        <div>
          <h2>Resumen por período</h2>
          <p>Invertido y ganancia de {formatMonthTitle(year, month)}.</p>
        </div>

        <div className={styles.Controls}>
          <label className={styles.FilterControl}>
            <span>Mes</span>

            <select value={month} onChange={handleMonthChange}>
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className={styles.ExportButton}
            onClick={handleExport}
            disabled={isLoading || items.length === 0}
          >
            <Icon name="download" />
            Exportar
          </button>
        </div>
      </header>

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      <div className={styles.Cards}>
        <article className={styles.Card}>
          {isLoading ? (
            <div className={styles.Skeleton} />
          ) : (
            <>
              <div className={styles.CardHeader}>
                <p className={styles.CardTitle}>Invertido</p>

                <div className={`${styles.CardIcon} ${styles.CardIconcost}`}>
                  <Icon name="shopping bag" />
                </div>
              </div>

              <p className={styles.CardValue}>{formatCurrency(totals.cost)}</p>

              <span className={styles.CardDescription}>
                costo de lo vendido en el período
              </span>
            </>
          )}
        </article>

        <article className={styles.Card}>
          {isLoading ? (
            <div className={styles.Skeleton} />
          ) : (
            <>
              <div className={styles.CardHeader}>
                <p className={styles.CardTitle}>Ganancia</p>

                <div
                  className={`${styles.CardIcon} ${
                    styles[
                      totals.profit >= 0
                        ? "CardIconprofit"
                        : "CardIcondanger"
                    ]
                  }`}
                >
                  <Icon
                    name={
                      totals.profit >= 0 ? "chart line" : "exclamation triangle"
                    }
                  />
                </div>
              </div>

              <p className={styles.CardValue}>
                {formatCurrency(totals.profit)}
              </p>

              <span className={styles.CardDescription}>
                ingresos ({formatCurrency(totals.revenue)}) - costo
              </span>
            </>
          )}
        </article>
      </div>

      {!isLoading && !error && hasData && (
        <div className={styles.Breakdown}>
          <div className={styles.BreakdownHeader}>
            <h3>Ventas por producto</h3>

            <span className={styles.BreakdownSubtitle}>
              {formatCurrency(totals.revenue)} en {totals.unitsSold}{" "}
              {totals.unitsSold === 1 ? "pieza vendida" : "piezas vendidas"}
            </span>
          </div>

          <div className={styles.BreakdownTableWrapper}>
            <table className={styles.BreakdownTable}>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Costo</th>
                  <th>Precio de venta</th>
                  <th>% del mes</th>
                </tr>
              </thead>

              <tbody>
                {visibleBreakdownRows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <div className={styles.ProductCell}>
                        {row.productImage ? (
                          <img
                            src={row.productImage}
                            alt=""
                            className={styles.ProductThumb}
                          />
                        ) : (
                          <span className={styles.ProductThumbPlaceholder}>
                            ▣
                          </span>
                        )}

                        <div>
                          <strong>{row.productName}</strong>
                          <span>
                            {row.sku} · {row.variantLabel}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>{row.quantity}</td>

                    <td>{formatCurrency(row.cost)}</td>

                    <td>{formatCurrency(row.revenue)}</td>

                    <td>
                      <div className={styles.ShareCell}>
                        <div className={styles.ShareBarTrack}>
                          <div
                            className={styles.ShareBarFill}
                            style={{
                              width: `${Math.min(row.revenueShare, 100)}%`,
                            }}
                          />
                        </div>

                        <span>{row.revenueShare.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className={styles.BreakdownTotalRow}>
                  <td>Total</td>
                  <td>{totals.unitsSold}</td>
                  <td>{formatCurrency(totals.cost)}</td>
                  <td>{formatCurrency(totals.revenue)}</td>
                  <td>100%</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {breakdownRows.length > BREAKDOWN_VISIBLE_LIMIT && (
            <button
              type="button"
              className={styles.ShowMoreButton}
              onClick={() => setShowAllItems((current) => !current)}
            >
              {showAllItems
                ? "Ver menos"
                : `Ver los ${breakdownRows.length} productos`}
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && !hasData && (
        <div className={styles.EmptyState}>
          <Icon name="calendar outline" />
          <span>No hubo salidas registradas en este período.</span>
        </div>
      )}
    </section>
  );
}
